import React from 'react';
import { Button } from '@/components/ui/button';
import { CreditCard, ExternalLink, PauseCircle, XCircle, PlayCircle, RotateCcw } from 'lucide-react';

export const SubscriptionSection = ({ 
  user, 
  onAddPayment, 
  addingPayment, 
  onManageSubscription, 
  openingPortal,
  onResubscribe,
  onOpenCancelModal,
  onResume,
  onUndoCancel,
  resuming,
  undoingCancel
}) => {
  const isPaused = user?.subscription_paused;
  const isCanceledPending = user?.cancel_at_period_end;
  const isActive = user?.subscription_status === 'active' && !user?.is_trialing;
  const hasPayment = user?.payment_added;

  return (
    <div className="bg-white/60 backdrop-blur-md border border-[#EAE4DC] shadow-lg rounded-2xl p-8">
      <div className="flex items-center space-x-3 mb-6">
        <CreditCard className="w-6 h-6 text-stone-600" strokeWidth={1.5} />
        <h2
          className="text-2xl font-medium"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          Subscription
        </h2>
      </div>
      
      {/* Trial User - Show Add Payment */}
      {user?.is_trialing && !user?.payment_added && (
        <div className="space-y-4">
          <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)' }}>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <span className="text-sm font-medium" style={{ color: '#a08242' }}>Free Trial Active</span>
            </div>
            <p className="text-stone-600 text-sm">
              {user?.trial_end ? (
                <>Trial ends: {new Date(user.trial_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</>
              ) : (
                <>Your 14-day free trial is active</>
              )}
            </p>
          </div>
          <p className="text-stone-600">
            Add a payment method to continue your healing journey after your trial ends. 
            Your {user?.selected_plan === 'annual' ? 'annual ($333/year)' : 'monthly ($37/month)'} plan will begin when your trial ends.
          </p>
          <Button
            onClick={onAddPayment}
            disabled={addingPayment}
            className="rounded-full px-6 text-[#1A1A2E] shadow-lg"
            style={{ backgroundColor: '#C9A84C' }}
            data-testid="add-payment-button"
          >
            {addingPayment ? (
              'Redirecting...'
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                Add Payment Method
              </>
            )}
          </Button>
        </div>
      )}
      
      {/* Paused Subscription */}
      {isPaused && (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
            <div className="flex items-center gap-2 mb-2">
              <PauseCircle className="w-5 h-5 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">Subscription Paused</span>
            </div>
            <p className="text-stone-600 text-sm">
              Your subscription is paused and will automatically resume on{' '}
              <strong>{new Date(user.pause_resumes_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
            </p>
          </div>
          <p className="text-stone-600">
            Your data and conversation history with Sage are safely preserved. Ready to continue your journey?
          </p>
          <Button
            onClick={onResume}
            disabled={resuming}
            className="rounded-full px-6 text-[#1A1A2E] shadow-lg"
            style={{ backgroundColor: '#C9A84C' }}
            data-testid="resume-subscription-button"
          >
            {resuming ? (
              'Resuming...'
            ) : (
              <>
                <PlayCircle className="w-4 h-4 mr-2" />
                Resume Now
              </>
            )}
          </Button>
        </div>
      )}

      {/* Canceled (pending end of period) */}
      {isCanceledPending && !isPaused && (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-red-50 border border-red-200">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="w-5 h-5 text-red-500" />
              <span className="text-sm font-medium text-red-800">Cancellation Scheduled</span>
            </div>
            <p className="text-stone-600 text-sm">
              Your subscription will end on{' '}
              <strong>{new Date(user.subscription_ends_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
            </p>
          </div>
          <p className="text-stone-600">
            Changed your mind? You can reactivate your subscription anytime before it ends.
          </p>
          <Button
            onClick={onUndoCancel}
            disabled={undoingCancel}
            className="rounded-full px-6 text-[#1A1A2E] shadow-lg"
            style={{ backgroundColor: '#C9A84C' }}
            data-testid="undo-cancel-button"
          >
            {undoingCancel ? (
              'Reactivating...'
            ) : (
              <>
                <RotateCcw className="w-4 h-4 mr-2" />
                Keep My Subscription
              </>
            )}
          </Button>
        </div>
      )}
      
      {/* Active Subscriber - Show Manage + Cancel/Pause */}
      {((isActive && !isPaused && !isCanceledPending) || (hasPayment && !isPaused && !isCanceledPending)) && (
        <div className="space-y-4">
          <p className="text-stone-600">
            Manage your billing, update payment method, or make changes to your subscription.
          </p>
          
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={onManageSubscription}
              disabled={openingPortal}
              variant="outline"
              className="rounded-full px-6"
              data-testid="manage-subscription-button"
            >
              {openingPortal ? (
                'Opening...'
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Billing Portal
                </>
              )}
            </Button>
            
            <Button
              onClick={onOpenCancelModal}
              variant="ghost"
              className="rounded-full px-6 text-stone-500 hover:text-red-600 hover:bg-red-50"
              data-testid="open-cancel-modal-button"
            >
              <PauseCircle className="w-4 h-4 mr-2" />
              Pause or Cancel
            </Button>
          </div>
          
          <p className="text-xs text-stone-400 mt-4">
            <strong>Refund Policy:</strong> 7-day refund from first charge. Annual plans prorated within 30 days. 
            Contact <a href="mailto:support@mindfulrenewal.com" className="underline">support@mindfulrenewal.com</a>
          </p>
        </div>
      )}
      
      {/* Inactive User - Show Resubscribe */}
      {user?.subscription_status === 'inactive' && !user?.is_trialing && !isPaused && !isCanceledPending && (
        <div className="space-y-4">
          <p className="text-stone-600">
            Your subscription is inactive. Resubscribe to continue your healing journey with Sage.
          </p>
          <Button
            onClick={onResubscribe}
            className="rounded-full px-6 text-[#1A1A2E]"
            style={{ backgroundColor: '#C9A84C' }}
            data-testid="resubscribe-button"
          >
            Resubscribe
          </Button>
        </div>
      )}
    </div>
  );
};
